/* eslint-disable */
import { useEffect, useState } from "react";
import api from "../lib/api";

function fmtMoney(n) {
  if (n == null) return "—";
  const v = Number(n) || 0;
  return v >= 1000
    ? `$${(v / 1000).toFixed(1)}k`
    : `$${v.toFixed(v % 1 === 0 ? 0 : 0)}`;
}
function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch { return iso; }
}

function StatCell({ value, label, testid, hint }) {
  return (
    <div className="m-stat" data-testid={testid}>
      <span className="m-stat-num">{value}</span>
      <span className="m-stat-label">{label}</span>
      {hint != null && <span className="m-stat-hint">{hint}</span>}
    </div>
  );
}

export default function AdminOverview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/admin/overview")
      .then(r => setData(r.data))
      .catch(e => setErr(e?.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="m-loading-inline" data-testid="overview-loading">Loading dashboard…</div>;
  if (err)     return <div className="m-empty" data-testid="overview-error">{err}</div>;
  if (!data)   return null;

  const br = data.bookings_revenue;
  const m  = data.members;
  const ha = data.host_applications;
  const ev = data.events;
  const fee = data.platform_fee_percent;
  const conv = br.conversion_rate_30d;

  return (
    <div className="m-admin-section" data-testid="overview-panel">

      {/* === 1. BOOKINGS & REVENUE === */}
      <div className="m-card m-overview-card">
        <div className="m-overview-h"><span className="m-eyebrow">Priority 1</span><h3>Bookings &amp; revenue</h3></div>
        <div className="m-stats-row m-overview-stats">
          <StatCell value={br.bookings_total} label="Bookings (all time)" testid="ov-bookings-total" hint={`+${br.bookings_30d} in last 30d`} />
          <StatCell value={fmtMoney(br.revenue_total)} label="Revenue (all time)" testid="ov-revenue-total" hint={`${fmtMoney(br.revenue_30d)} last 30d`} />
          <StatCell value={fmtMoney(br.platform_fees_total)} label={`Métier fees (${fee}%)`} testid="ov-fees-total" hint={`${fmtMoney(br.platform_fees_30d)} last 30d`} />
          <StatCell value={fmtMoney(br.host_payouts_total)} label="Host payouts" testid="ov-payouts-total" hint={`${fmtMoney(br.host_payouts_30d)} last 30d`} />
        </div>
        <div className="m-overview-row">
          <div className="m-overview-col">
            <div className="m-overview-subtitle">Booking mix · all time</div>
            <table className="m-utm-table">
              <tbody>
                <tr><td>Hourly</td><td style={{textAlign:"right"}}><strong>{br.duration_buckets.hourly}</strong></td></tr>
                <tr><td>Daily</td><td style={{textAlign:"right"}}><strong>{br.duration_buckets.daily}</strong></td></tr>
                <tr><td>Weekly</td><td style={{textAlign:"right"}}><strong>{br.duration_buckets.weekly}</strong></td></tr>
                <tr><td>Monthly</td><td style={{textAlign:"right"}}><strong>{br.duration_buckets.monthly}</strong></td></tr>
              </tbody>
            </table>
            <div className="m-overview-subtitle" style={{marginTop:14}}>Conversion · 30d (bookers / active)</div>
            <div className="m-overview-mega" data-testid="ov-conv-rate">{conv == null ? "—" : `${(conv*100).toFixed(1)}%`}</div>
          </div>
          <div className="m-overview-col">
            <div className="m-overview-subtitle">Most booked listings</div>
            {br.top_workspaces.length === 0 ? (
              <div className="m-muted small">No bookings yet.</div>
            ) : (
              <table className="m-utm-table" data-testid="ov-top-workspaces">
                <thead><tr><th>Listing</th><th style={{textAlign:"right"}}>Bookings</th><th>Last</th></tr></thead>
                <tbody>
                  {br.top_workspaces.map(r => (
                    <tr key={r.workspace_slug}>
                      <td>{r.workspace_slug}</td>
                      <td style={{textAlign:"right"}}><strong>{r.bookings}</strong></td>
                      <td className="m-muted small">{fmtDate(r.last_booking)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* === 2. MEMBERS === */}
      <div className="m-card m-overview-card">
        <div className="m-overview-h"><span className="m-eyebrow">Priority 2</span><h3>Member growth</h3></div>
        <div className="m-stats-row m-overview-stats">
          <StatCell value={m.total}        label="Members"            testid="ov-members-total" />
          <StatCell value={`+${m.new_7d}`} label="New · 7d"            testid="ov-members-7d" />
          <StatCell value={`+${m.new_30d}`} label="New · 30d"          testid="ov-members-30d" />
          <StatCell
            value={m.growth_rate_30d_pct == null ? "—" : `${m.growth_rate_30d_pct > 0 ? "+" : ""}${m.growth_rate_30d_pct}%`}
            label="Growth · 30d vs prior"
            testid="ov-growth"
          />
          <StatCell value={m.founding_count} label="Founding" testid="ov-founding" />
        </div>
        <div className="m-overview-row">
          <div className="m-overview-col">
            <div className="m-overview-subtitle">By city / state</div>
            <table className="m-utm-table" data-testid="ov-by-city">
              <thead><tr><th>Location</th><th style={{textAlign:"right"}}>Members</th></tr></thead>
              <tbody>
                {m.by_city.map(r => (
                  <tr key={r.city}><td>{r.city}</td><td style={{textAlign:"right"}}><strong>{r.members}</strong></td></tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="m-overview-col">
            <div className="m-overview-subtitle">Most active (by bookings)</div>
            {m.most_active.length === 0 ? (
              <div className="m-muted small">No member bookings yet.</div>
            ) : (
              <table className="m-utm-table" data-testid="ov-most-active">
                <thead><tr><th>Member</th><th style={{textAlign:"right"}}>Bookings</th></tr></thead>
                <tbody>
                  {m.most_active.map(u => (
                    <tr key={u.user_id}>
                      <td>{u.name} <span className="m-muted small">· {u.city || "—"}</span></td>
                      <td style={{textAlign:"right"}}><strong>{u.bookings}</strong></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* === 3. HOST APPLICATIONS === */}
      <div className="m-card m-overview-card">
        <div className="m-overview-h"><span className="m-eyebrow">Priority 3</span><h3>Host pipeline</h3></div>
        <div className="m-stats-row m-overview-stats">
          <StatCell value={ha.pending}  label="Pending"  testid="ov-apps-pending" hint="action needed" />
          <StatCell value={ha.approved} label="Approved" testid="ov-apps-approved" />
          <StatCell value={ha.rejected} label="Rejected" testid="ov-apps-rejected" />
          <StatCell value={ha.total}    label="All time" testid="ov-apps-total" />
          <StatCell value={ha.workspaces_dormant_30d.length} label="Dormant 30d" testid="ov-dormant" hint="no bookings" />
        </div>
        <div className="m-overview-row">
          <div className="m-overview-col">
            <div className="m-overview-subtitle">Recent pending applications</div>
            {ha.recent.length === 0 ? (
              <div className="m-muted small">No pending applications.</div>
            ) : (
              <table className="m-utm-table" data-testid="ov-recent-apps">
                <thead><tr><th>Space</th><th>Where</th><th>Contact</th><th>When</th></tr></thead>
                <tbody>
                  {ha.recent.map(a => (
                    <tr key={a.application_id}>
                      <td>{a.name}</td>
                      <td className="m-muted small">{a.neighborhood} · {a.city}</td>
                      <td className="m-muted small">{a.contact_email}</td>
                      <td className="m-muted small">{fmtDate(a.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <div className="m-overview-col">
            <div className="m-overview-subtitle">Workspaces with no bookings in 30d</div>
            {ha.workspaces_dormant_30d.length === 0 ? (
              <div className="m-muted small">All listings are active.</div>
            ) : (
              <table className="m-utm-table" data-testid="ov-dormant-list">
                <tbody>
                  {ha.workspaces_dormant_30d.map(w => (
                    <tr key={w.slug}><td>{w.name}</td><td className="m-muted small">{w.city}</td></tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* === 4. EVENTS === */}
      <div className="m-card m-overview-card">
        <div className="m-overview-h"><span className="m-eyebrow">Priority 4</span><h3>Community events</h3></div>
        <div className="m-stats-row m-overview-stats">
          <StatCell value={ev.pending}  label="Pending" testid="ov-events-pending" hint="action needed" />
          <StatCell value={ev.upcoming} label="Upcoming" testid="ov-events-upcoming" />
          <StatCell value={ev.total}    label="All time" testid="ov-events-total" />
          <StatCell
            value={(ev.rsvps || []).reduce((s, r) => s + (r.rsvp_count || 0), 0)}
            label="RSVPs · upcoming"
            testid="ov-events-rsvps"
            hint={`${(ev.rsvps || []).length} events`}
          />
        </div>
        <div className="m-overview-row">
          <div className="m-overview-col">
            <div className="m-overview-subtitle">Upcoming · RSVPs</div>
            {(ev.rsvps && ev.rsvps.length) ? (
              <table className="m-utm-table" data-testid="ov-rsvp-list">
                <thead><tr><th>Event</th><th>Date</th><th style={{textAlign:"right"}}>RSVPs</th></tr></thead>
                <tbody>
                  {ev.rsvps.map(r => (
                    <tr key={r.event_id || r.title}>
                      <td>{r.title}</td>
                      <td className="m-muted small">{r.date}</td>
                      <td style={{textAlign:"right"}}><strong>{r.rsvp_count || 0}</strong></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <div className="m-muted small">No upcoming events.</div>}
          </div>
          <div className="m-overview-col">
            <div className="m-overview-subtitle">Recent</div>
            {ev.recent.length === 0 ? (
              <div className="m-muted small">No events yet.</div>
            ) : (
              <table className="m-utm-table" data-testid="ov-recent-events">
                <thead><tr><th>Title</th><th>Date</th><th>Location</th></tr></thead>
                <tbody>
                  {ev.recent.map(e => (
                    <tr key={e.event_id}><td>{e.title}</td><td className="m-muted small">{e.date}</td><td className="m-muted small">{e.location}</td></tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* === 5. PARTNER OUT-CLICKS === */}
      {data.partner_clicks && (
        <div className="m-card m-overview-card">
          <div className="m-overview-h"><span className="m-eyebrow">Priority 5</span><h3>Partner out-clicks</h3></div>
          <div className="m-stats-row m-overview-stats">
            <StatCell value={data.partner_clicks.total}     label="All-time clicks" testid="ov-clicks-total" hint={`+${data.partner_clicks.clicks_7d} last 7d`} />
            <StatCell value={data.partner_clicks.clicks_30d} label="Last 30d" testid="ov-clicks-30d" />
            <StatCell value={(data.partner_clicks.by_campaign || []).length} label="Active campaigns" testid="ov-clicks-campaigns" />
          </div>
          <div className="m-overview-row">
            <div className="m-overview-col">
              <div className="m-overview-subtitle">By campaign (UTM)</div>
              {(data.partner_clicks.by_campaign || []).length === 0 ? (
                <div className="m-muted small">No partner clicks tracked yet.</div>
              ) : (
                <table className="m-utm-table" data-testid="ov-clicks-by-campaign">
                  <thead><tr><th>Campaign</th><th style={{textAlign:"right"}}>Clicks</th></tr></thead>
                  <tbody>
                    {data.partner_clicks.by_campaign.map(r => (
                      <tr key={r.campaign}><td>{r.campaign}</td><td style={{textAlign:"right"}}><strong>{r.clicks}</strong></td></tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="m-overview-col">
              <div className="m-overview-subtitle">Top partner spaces</div>
              {(data.partner_clicks.top_workspaces || []).length === 0 ? (
                <div className="m-muted small">No partner clicks yet.</div>
              ) : (
                <table className="m-utm-table" data-testid="ov-clicks-top">
                  <thead><tr><th>Workspace</th><th style={{textAlign:"right"}}>Clicks</th><th>Last</th></tr></thead>
                  <tbody>
                    {data.partner_clicks.top_workspaces.map(r => (
                      <tr key={r.workspace_slug}>
                        <td>{r.workspace_slug}</td>
                        <td style={{textAlign:"right"}}><strong>{r.clicks}</strong></td>
                        <td className="m-muted small">{fmtDate(r.last_click)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* === 6. SUPPORT TICKETS === */}
      {data.support && (
        <div className="m-card m-overview-card">
          <div className="m-overview-h"><span className="m-eyebrow">Priority 6</span><h3>Customer support</h3></div>
          <div className="m-stats-row m-overview-stats">
            <StatCell value={data.support.open}        label="Open"        testid="ov-tickets-open" hint="needs reply" />
            <StatCell value={data.support.total}       label="All time"    testid="ov-tickets-total" />
            <StatCell value={data.support.tickets_30d} label="Last 30d"    testid="ov-tickets-30d" />
            <StatCell value={data.support.tickets_7d}  label="Last 7d"     testid="ov-tickets-7d" />
          </div>
          <div className="m-overview-row">
            <div className="m-overview-col">
              <div className="m-overview-subtitle">By category</div>
              {(data.support.by_category || []).length === 0 ? (
                <div className="m-muted small">No tickets yet.</div>
              ) : (
                <table className="m-utm-table" data-testid="ov-tickets-by-cat">
                  <thead><tr><th>Category</th><th style={{textAlign:"right"}}>Count</th></tr></thead>
                  <tbody>
                    {data.support.by_category.map(r => (
                      <tr key={r.category}><td>{r.category}</td><td style={{textAlign:"right"}}><strong>{r.count}</strong></td></tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="m-overview-col">
              <div className="m-overview-subtitle">Recent tickets</div>
              {(data.support.recent || []).length === 0 ? (
                <div className="m-muted small">No recent tickets.</div>
              ) : (
                <table className="m-utm-table" data-testid="ov-tickets-recent">
                  <thead><tr><th>Subject</th><th>Category</th><th>When</th></tr></thead>
                  <tbody>
                    {data.support.recent.map(t => (
                      <tr key={t.ticket_id}>
                        <td>{t.subject}</td>
                        <td className="m-muted small">{t.category}</td>
                        <td className="m-muted small">{fmtDate(t.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* === 7. DOUBLE-BOOKING INCIDENTS === */}
      {data.incidents && (
        <div className="m-card m-overview-card">
          <div className="m-overview-h"><span className="m-eyebrow">Priority 7</span><h3>Booking incidents</h3></div>
          <div className="m-stats-row m-overview-stats">
            <StatCell
              value={data.incidents.double_booking_count}
              label="Double bookings (detected)"
              testid="ov-incidents-count"
              hint={data.incidents.double_booking_count ? "review immediately" : "clean"}
            />
          </div>
          {data.incidents.double_booking_count === 0 ? (
            <div className="m-muted small" data-testid="ov-incidents-clean">No overlapping confirmed bookings detected.</div>
          ) : (
            <table className="m-utm-table" data-testid="ov-incidents-list">
              <thead><tr><th>Workspace</th><th>Booking A</th><th>Booking B</th><th>Overlap</th></tr></thead>
              <tbody>
                {data.incidents.double_bookings.map((i, idx) => (
                  <tr key={idx}>
                    <td>{i.workspace_slug}</td>
                    <td className="m-muted small">{i.booking_a} <span className="m-muted small">· {i.user_a}</span></td>
                    <td className="m-muted small">{i.booking_b} <span className="m-muted small">· {i.user_b}</span></td>
                    <td className="m-muted small">{fmtDate(i.overlap_start)} → {fmtDate(i.overlap_end)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
